/**
 * Operators for creating pipeable string transforms
 */

import { capitalCase } from 'change-case';

import { StringTransform } from '../types';
import { methodToOperator } from '../utils';

// Built-in string methods

/* eslint-disable @typescript-eslint/unbound-method */
export const replace = methodToOperator<[string | RegExp, string]>(
  String.prototype.replace,
);
export const toLowerCase = methodToOperator(String.prototype.toLowerCase);
export const toUpperCase = methodToOperator(String.prototype.toUpperCase);
export const trim = methodToOperator(String.prototype.trim);
export const trimStart = methodToOperator(String.prototype.trimStart);
export const trimEnd = methodToOperator(String.prototype.trimEnd);
export const padStart = methodToOperator<[number, string?]>(
  String.prototype.padStart,
);
export const padEnd = methodToOperator<[number, string?]>(
  String.prototype.padEnd,
);
export const repeat = methodToOperator<[number]>(String.prototype.repeat);
export const substring = methodToOperator<[number, number?]>(
  String.prototype.substring,
);
/* eslint-enable @typescript-eslint/unbound-method */

// Custom transformers

/**
 * Returns a transform that truncates a string to a specified length, optionally adding a suffix if truncated.
 * The length of the suffix is included in the total length maximum.
 *
 * @param maxLength The maximum length of the truncated string.
 * @param truncatedSuffix If supplied, a suffix that indicates that the string was truncated, such as an ellipsis.
 * @returns A transform that truncates a string to the specified length.
 */
export function truncate(
  maxLength: number,
  truncatedSuffix?: string,
): StringTransform {
  return ($$) => {
    if ($$.length <= maxLength) {
      return $$;
    }
    truncatedSuffix = truncatedSuffix ?? '';
    return (
      $$.slice(0, Math.max(0, maxLength - truncatedSuffix.length)) +
      truncatedSuffix
    );
  };
}

/**
 * Returns a transform that capitalizes the first character of a string.
 * @returns A transform that capitalizes the first character.
 */
export function capitalize(): StringTransform {
  return ($$) =>
    $$.length === 0 ? $$ : $$.charAt(0).toUpperCase() + $$.slice(1);
}

/**
 * Returns a transform that converts a string to title case (first letter of each word capitalized).
 * @returns A transform that converts to title case.
 */
export function titleCase(): StringTransform {
  return ($$) => capitalCase($$).replace(/([A-Za-z])([0-9])/g, '$1 $2');
}

/**
 * Returns a transform that adds a prefix to the string.
 * @param prefix The prefix to add.
 * @returns A transform that adds the prefix.
 */
export function prepend(prefix: string): StringTransform {
  return ($$) => prefix + $$;
}

/**
 * Returns a transform that adds a suffix to the string.
 * @param suffix The suffix to add.
 * @returns A transform that adds the suffix.
 */
export function append(suffix: string): StringTransform {
  return ($$) => $$ + suffix;
}

/**
 * Returns a transform that removes a prefix from the string if present.
 * @param prefix The prefix to remove.
 * @returns A transform that removes the prefix.
 */
export function removePrefix(prefix: string): StringTransform {
  return ($$) => ($$.startsWith(prefix) ? $$.slice(prefix.length) : $$);
}

/**
 * Returns a transform that removes a suffix from the string if present.
 * @param suffix The suffix to remove.
 * @returns A transform that removes the suffix.
 */
export function removeSuffix(suffix: string): StringTransform {
  return ($$) =>
    suffix.length > 0 && $$.endsWith(suffix) ? $$.slice(0, -suffix.length) : $$;
}
